import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CreditCard,
  Plus,
  Download,
  X,
  Save,
  Trash2,
  Archive,
  ArchiveRestore,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  Settings,
  Upload,
  Target,
  DollarSign,
  Percent,
  Sparkles,
  FileSearch,
  Shield,
  Zap,
  CreditCard as CreditCardIcon,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;
const UP = 'creditBuilder.utilizationPlanner';

const SOURCE_LABEL_DEFAULTS = {
  Report: 'Credit Report Imported',
  Imported: 'Credit Report Imported',
  Manual: 'Manually Imported',
  Demo: 'Demo',
};

const getSourceLabel = (source, t) => {
  if (source === 'Report' || source === 'Imported') {
    return t(`${UP}.sourceReport`, null, SOURCE_LABEL_DEFAULTS.Report);
  }
  if (source === 'Manual') {
    return t(`${UP}.sourceManual`, null, SOURCE_LABEL_DEFAULTS.Manual);
  }
  if (source === 'Demo') {
    return t(`${UP}.sourceDemo`, null, SOURCE_LABEL_DEFAULTS.Demo);
  }
  return source || 'Unknown';
};

const getSourceBadgeClass = (source) => {
  if (source === 'Report' || source === 'Imported') {
    return 'bg-[#eaf8f0] dark:bg-[rgba(16,185,129,0.20)] text-[#107649] dark:text-emerald-300';
  }
  if (source === 'Demo') {
    return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
  }
  return 'bg-[#eef6ff] dark:bg-[rgba(59,130,246,0.20)] text-[#245d9c] dark:text-blue-300';
};

// ===================== HELPER FUNCTIONS =====================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

const formatFullCurrency = (amount) => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(amount));
};

const formatCompactCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(1)}k`;
  }
  return formatCurrency(num);
};

const formatShortMonthDay = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
  } catch {
    return '—';
  }
};

const calcPayByDate = (statementDate) => {
  if (!statementDate) return null;
  try {
    const d = new Date(statementDate + 'T00:00:00');
    d.setDate(d.getDate() - 3);
    return d.toISOString().slice(0, 10);
  } catch {
    return null;
  }
};

const getPayByDisplay = (statementDate) => {
  const payByDate = calcPayByDate(statementDate);
  if (!payByDate) {
    return {
      label: '—',
      className: 'text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap',
      status: 'none',
    };
  }

  try {
    const target = new Date(payByDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (target.getTime() === today.getTime()) {
      return {
        label: formatShortMonthDay(payByDate),
        className: 'text-[11px] sm:text-xs font-semibold text-amber-700 dark:text-amber-300 whitespace-nowrap',
        status: 'today',
      };
    }

    if (target.getTime() < today.getTime()) {
      return {
        label: formatShortMonthDay(payByDate),
        className: 'text-[11px] sm:text-xs font-semibold text-red-600 dark:text-red-300 whitespace-nowrap',
        status: 'overdue',
      };
    }

    return {
      label: formatShortMonthDay(payByDate),
      className: 'text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap',
      status: 'upcoming',
    };
  } catch {
    return {
      label: '—',
      className: 'text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap',
      status: 'none',
    };
  }
};

const sanitizeMoneyInput = (value) => {
  if (value == null) return '';
  let cleaned = String(value).replace(/[$,\s]/g, '').replace(/[^\d.]/g, '');
  const dotIndex = cleaned.indexOf('.');
  if (dotIndex !== -1) {
    cleaned = cleaned.slice(0, dotIndex + 1) + cleaned.slice(dotIndex + 1).replace(/\./g, '');
  }
  if (cleaned.startsWith('.')) {
    cleaned = `0${cleaned}`;
  }
  return cleaned;
};

const normalizeMoneyInput = (value) => {
  if (value == null || value === '') return '';
  const cleaned = sanitizeMoneyInput(value);
  if (cleaned === '' || cleaned === '.') return '';

  const [rawInt = '0', decPart] = cleaned.split('.');
  const intPart = rawInt.replace(/^0+(?=\d)/, '') || '0';

  if (decPart !== undefined) {
    return `${intPart}.${decPart}`;
  }
  return intPart;
};

const parseMoneyValue = (value) => {
  const normalized = normalizeMoneyInput(value);
  if (normalized === '') return 0;
  const num = parseFloat(normalized);
  return Number.isFinite(num) ? num : 0;
};

const formatMoneyForInput = (value) => {
  if (value == null || value === '') return '';
  return String(value);
};

const MONEY_INPUT_CLASS = 'h-9 sm:h-11 rounded-xl sm:rounded-[14px] text-sm dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:text-[#EAF7F0]';
const DATE_INPUT_CLASS = 'h-9 sm:h-11 rounded-xl sm:rounded-[14px] text-sm dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:text-[#EAF7F0] utilization-date-input';

const calcPayTo1Percent = (balance, limit) => {
  const bal = parseFloat(balance) || 0;
  const lim = parseFloat(limit) || 0;
  if (lim <= 0) return Math.max(0, bal);
  const target1Percent = lim * 0.01;
  return Math.max(0, bal - target1Percent);
};

const isClosedWithBalance = (card) => {
  const balance = parseFloat(card?.balance) || 0;
  if (balance <= 0) return false;
  return card?.health_status === 'Closed w/ Balance'
    || card?.account_status === 'closed'
    || card?.exclusion_reason === 'closed_with_balance'
    || (card?.included_in_utilization === false && balance > 0);
};

const isOpenOrClosedWithBalance = (card) => {
  if (card?.account_status === 'open' && card?.included_in_utilization !== false) return true;
  return isClosedWithBalance(card);
};

const calcPayToGoal = (balance, limit, goalPercent) => {
  const bal = parseFloat(balance) || 0;
  const lim = parseFloat(limit) || 0;
  const gp = parseFloat(goalPercent) || 9;
  const targetBalance = lim * (gp / 100);
  return Math.max(0, bal - targetBalance);
};

// ===================== STATUS CONFIGS =====================
const getStatusConfig = (utilization, card, t) => {
  if (card && (card.account_status === 'closed' || card.exclusion_reason === 'closed_account' || card.exclusion_reason === 'closed_with_balance')) {
    if (card.exclusion_reason === 'closed_with_balance') {
      return { label: t(`${UP}.statusClosedWithBalance`), color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300', barColor: '#d97706' };
    }
    return { label: t(`${UP}.statusClosed`), color: 'bg-stone-200 dark:bg-stone-800/50 text-stone-600 dark:text-stone-300', barColor: '#78716c' };
  }
  if (card && card.included_in_utilization === false) {
    return { label: t(`${UP}.statusExcluded`), color: 'bg-stone-200 dark:bg-stone-800/50 text-stone-600 dark:text-stone-300', barColor: '#78716c' };
  }
  const util = parseFloat(utilization) || 0;
  if (util > 100) return { label: t(`${UP}.statusOverLimit`), color: 'bg-[#ffd8d8] dark:bg-red-900/30 text-[#b42318] dark:text-red-300', barColor: '#b42318' };
  if (util >= 90) return { label: t(`${UP}.statusHigh`), color: 'bg-[#ffe1e1] dark:bg-red-900/30 text-[#c53a3a] dark:text-red-300', barColor: '#c53a3a' };
  if (util > 30) return { label: t(`${UP}.statusNeedsPaydown`), color: 'bg-[#ffe6d8] dark:bg-orange-900/30 text-[#b95400] dark:text-orange-300', barColor: '#d97706' };
  if (util > 9) return { label: t(`${UP}.statusOkay`), color: 'bg-[#fff3db] dark:bg-amber-900/30 text-[#9a6800] dark:text-amber-300', barColor: '#eab308' };
  return { label: t(`${UP}.statusHealthy`), color: 'bg-[#dff7ea] dark:bg-emerald-900/30 text-[#107649] dark:text-emerald-300', barColor: '#10b981' };
};

const getAccountStatusLabel = (card, t) => {
  if (card?.account_status_label) return card.account_status_label;
  const status = (card?.account_status || 'unknown').toLowerCase();
  if (status === 'open') return t(`${UP}.accountStatusOpen`, null, 'Open');
  if (status === 'closed') return t(`${UP}.accountStatusClosed`, null, 'Closed');
  return t(`${UP}.accountStatusUnknown`, null, 'Unknown');
};

const getAccountStatusBadgeClass = (label) => {
  const normalized = (label || '').toLowerCase();
  if (normalized === 'open') return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
  if (normalized === 'paid') return 'bg-stone-100 dark:bg-stone-800/50 text-stone-600 dark:text-stone-300';
  if (normalized === 'transferred') return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
  if (normalized === 'closed') return 'bg-stone-200 dark:bg-stone-800/50 text-stone-600 dark:text-stone-300';
  return 'bg-stone-100 dark:bg-stone-800/40 text-stone-500 dark:text-stone-400';
};

const getHealthStatusConfig = (card, t) => {
  const health = (card?.health_status || card?.status || '').replace(' Utilization', '');
  const balance = parseFloat(card?.balance) || 0;

  if (health === 'Closed w/ Balance' || (balance > 0 && (
    card?.account_status === 'closed'
    || card?.exclusion_reason === 'closed_with_balance'
    || card?.account_status_label === 'Closed'
  ) && card?.included_in_utilization === false)) {
    return {
      label: t(`${UP}.statusClosedWithBalance`, null, 'Closed w/ Balance'),
      color: '',
      badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
      tooltip: t(`${UP}.closedBalanceTooltip`, null, 'Closed card limit is not counted, but the balance still affects utilization.'),
    };
  }

  if (health === 'Inactive' || (balance === 0 && ['Closed', 'Paid', 'Transferred'].includes(card?.account_status_label))) {
    return { label: '—', color: 'text-stone-400 dark:text-stone-500', badge: 'bg-transparent text-stone-400 dark:text-stone-500', tooltip: null };
  }

  if (card && card.included_in_utilization === false && balance === 0) {
    return { label: '—', color: 'text-stone-400 dark:text-stone-500', badge: 'bg-transparent text-stone-400 dark:text-stone-500', tooltip: null };
  }

  if (health === 'Over Limit') return { label: t(`${UP}.statusOverLimit`, null, 'Over Limit'), color: '', badge: 'bg-[#ffd8d8] dark:bg-red-900/30 text-[#b42318] dark:text-red-300', tooltip: null };
  if (health === 'High' || health === 'High Utilization') return { label: t(`${UP}.statusHigh`, null, 'High'), color: '', badge: 'bg-[#ffe1e1] dark:bg-red-900/30 text-[#c53a3a] dark:text-red-300', tooltip: null };
  if (health === 'Needs Paydown') return { label: t(`${UP}.statusNeedsPaydown`, null, 'Needs Paydown'), color: '', badge: 'bg-[#ffe6d8] dark:bg-orange-900/30 text-[#b95400] dark:text-orange-300', tooltip: null };
  if (health === 'Okay') return { label: t(`${UP}.statusOkay`, null, 'Okay'), color: '', badge: 'bg-[#fff3db] dark:bg-amber-900/30 text-[#9a6800] dark:text-amber-300', tooltip: null };
  if (health === 'Needs Attention') return { label: t(`${UP}.statusNeedsAttention`, null, 'Needs Attention'), color: '', badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300', tooltip: t(`${UP}.closedBalanceTooltip`, null, 'Closed card limit is not counted, but the balance still affects utilization.') };
  return { label: t(`${UP}.statusHealthy`, null, 'Healthy'), color: '', badge: 'bg-[#dff7ea] dark:bg-emerald-900/30 text-[#107649] dark:text-emerald-300', tooltip: null };
};

const getSetupStatusConfig = (setupStatus, t) => {
  switch (setupStatus) {
    case 'Needs Attention':
      return { label: t(`${UP}.setupNeedsAttention`, null, 'Needs Attention'), color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700/50', icon: AlertCircle, priority: 0 };
    case 'Needs Identification':
      return { label: t(`${UP}.setupIdentifyCard`), color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-700/50', icon: AlertCircle, priority: 1 };
    case 'Needs Statement Date':
      return { label: t(`${UP}.setupAddStatementDate`), color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700/50', icon: Clock, priority: 2 };
    case 'Needs Due Date':
      return { label: t(`${UP}.setupAddDueDate`), color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700/50', icon: Clock, priority: 3 };
    case 'Needs Review':
      return { label: t(`${UP}.setupReview`), color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700/50', icon: FileText, priority: 4 };
    case 'Ready':
    default:
      return { label: t(`${UP}.setupReady`), color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/50', icon: CheckCircle, priority: 5 };
  }
};

const getIssuerGradient = (issuerName) => {
  const issuer = (issuerName || '').toLowerCase();
  if (issuer.includes('chase')) return 'linear-gradient(135deg, #064e3b 0%, #065f46 45%, #047857 100%)';
  if (issuer.includes('amex') || issuer.includes('american express')) return 'linear-gradient(135deg, #0a6a40 0%, #0d8d54 45%, #10b981 100%)';
  if (issuer.includes('capital one')) return 'linear-gradient(135deg, #115e59 0%, #0d9488 45%, #14b8a6 100%)';
  if (issuer.includes('discover')) return 'linear-gradient(135deg, #064e3b 0%, #059669 45%, #34d399 100%)';
  if (issuer.includes('citi')) return 'linear-gradient(135deg, #0f766e 0%, #14b8a6 45%, #2dd4bf 100%)';
  return 'linear-gradient(135deg, #0a6a40 0%, #0d8d54 45%, #18a563 100%)';
};

// ===================== UTILIZATION POSITION THRESHOLDS =====================
const UTILIZATION_POSITION_DEFS = [
  { key: 'positionExceptional', descKey: 'positionExceptionalDesc', minUtil: 0, maxUtil: 9, color: 'emerald' },
  { key: 'positionVeryGood', descKey: 'positionVeryGoodDesc', minUtil: 10, maxUtil: 29, color: 'green' },
  { key: 'positionGood', descKey: 'positionGoodDesc', minUtil: 30, maxUtil: 49, color: 'lime' },
  { key: 'positionFair', descKey: 'positionFairDesc', minUtil: 50, maxUtil: 74, color: 'amber' },
  { key: 'positionPoor', descKey: 'positionPoorDesc', minUtil: 75, maxUtil: 100, color: 'red' },
];

const getUtilizationPositions = (t) =>
  UTILIZATION_POSITION_DEFS.map((pos) => ({
    ...pos,
    label: t(`${UP}.${pos.key}`),
    description: t(`${UP}.${pos.descKey}`),
  }));

const getUtilizationPosition = (utilization, t) => {
  const util = parseFloat(utilization) || 0;
  const positions = getUtilizationPositions(t);
  for (const pos of positions) {
    if (util >= pos.minUtil && util <= pos.maxUtil) return pos;
  }
  return positions[4];
};

// ===================== GLASS CARD COMPONENT =====================
const GlassCard = ({ children, className = '', highlight = false }) => (
  <div className={`
    relative overflow-hidden rounded-2xl sm:rounded-[28px] 
    ${highlight 
      ? 'bg-gradient-to-br from-white/80 via-emerald-50/70 to-white/60 dark:from-emerald-950/60 dark:via-emerald-900/40 dark:to-emerald-950/50 border border-emerald-500/20 dark:border-emerald-400/20' 
      : 'bg-white/70 dark:bg-emerald-950/40 border border-emerald-900/10 dark:border-emerald-400/10'
    }
    shadow-[0_16px_48px_rgba(6,43,27,0.08)] sm:shadow-[0_24px_70px_rgba(6,43,27,0.10)] dark:shadow-[0_24px_70px_rgba(0,0,0,0.30)] backdrop-blur-xl
    ${className}
  `}>
    <div className="absolute inset-[1px] rounded-[inherit] bg-gradient-to-br from-white/70 to-transparent dark:from-white/5 dark:to-transparent pointer-events-none" />
    <div className="relative z-10">{children}</div>
  </div>
);

// ===================== UTILIZATION BAR =====================
const UtilizationBar = ({ utilization, compact = false }) => {
  const { t } = useI18n();
  const util = Math.min(parseFloat(utilization) || 0, 120);
  const clampedUtil = Math.min(util, 100);
  const getBarColor = (position) => {
    if (position <= 9) return '#10b981';
    if (position <= 30) return '#eab308';
    if (position <= 90) return '#f97316';
    return '#ef4444';
  };
  const barColor = getBarColor(util);
  
  return (
    <div className={`w-full ${compact ? '' : 'px-0.5 sm:px-1'}`}>
      <div className="relative h-2 sm:h-2.5 bg-[#f0f4f2] dark:bg-emerald-900/40 rounded-full overflow-hidden">
        <div className="absolute top-0 left-[1%] h-full w-[8%] bg-emerald-100/60 dark:bg-emerald-700/30 rounded-full" style={{ zIndex: 0 }} />
        <div className="absolute top-0 left-0 h-full rounded-full transition-all duration-300" style={{ width: `${clampedUtil}%`, backgroundColor: barColor, zIndex: 1 }} />
        <div className="absolute top-0 h-full w-0.5 bg-emerald-600/40" style={{ left: '1%', zIndex: 2 }} />
        <div className="absolute top-0 h-full w-0.5 bg-emerald-600/30" style={{ left: '9%', zIndex: 2 }} />
      </div>
      {!compact && (
        <div className="flex justify-between mt-1 text-[9px] sm:text-[10px] text-stone-400 dark:text-emerald-300/60 font-medium">
          <span>{t(`${UP}.target1Percent`)}</span>
          <span>{t(`${UP}.safe1to9`)}</span>
        </div>
      )}
    </div>
  );
};

// ===================== UTILIZATION POSITION CARDS =====================
const UtilizationPositionCards = ({ currentUtilization }) => {
  const { t } = useI18n();
  const positions = getUtilizationPositions(t);
  const currentPosition = getUtilizationPosition(currentUtilization, t);
  
  const colorClasses = {
    emerald: { active: 'bg-gradient-to-br from-emerald-100/95 to-white/80 dark:from-emerald-900/60 dark:to-emerald-950/40 border-emerald-500/50 shadow-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-300' },
    green: { active: 'bg-gradient-to-br from-green-100/95 to-white/80 dark:from-green-900/60 dark:to-green-950/40 border-green-500/50 shadow-green-500/20', text: 'text-green-700 dark:text-green-300' },
    lime: { active: 'bg-gradient-to-br from-lime-100/95 to-white/80 dark:from-lime-900/60 dark:to-lime-950/40 border-lime-500/50 shadow-lime-500/20', text: 'text-lime-700 dark:text-lime-300' },
    amber: { active: 'bg-gradient-to-br from-amber-100/95 to-white/80 dark:from-amber-900/60 dark:to-amber-950/40 border-amber-500/50 shadow-amber-500/20', text: 'text-amber-700 dark:text-amber-300' },
    red: { active: 'bg-gradient-to-br from-red-100/95 to-white/80 dark:from-red-900/60 dark:to-red-950/40 border-red-500/50 shadow-red-500/20', text: 'text-red-700 dark:text-red-300' },
  };
  
  return (
    <div className="mt-4 sm:mt-5">
      <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
        <Target size={14} className="text-emerald-600 dark:text-emerald-400 sm:w-4 sm:h-4" />
        <h3 className="text-xs sm:text-sm font-bold text-stone-800 dark:text-emerald-50">{t(`${UP}.utilizationPosition`)}</h3>
        <span className="text-[10px] sm:text-xs text-stone-500 dark:text-emerald-300/60 ml-auto hidden sm:inline">{t(`${UP}.ficoImpactApprox`)}</span>
      </div>
      {/* Mobile: Horizontal scroll, Desktop: Grid */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-5 scrollbar-thin scrollbar-thumb-emerald-200 dark:scrollbar-thumb-emerald-800">
        {positions.slice().reverse().map((pos) => {
          const isActive = currentPosition.label === pos.label;
          return (
            <div 
              key={pos.label} 
              className={`
                flex-shrink-0 w-[85px] sm:w-auto min-w-0 p-2.5 sm:p-3 rounded-xl border backdrop-blur-sm transition-all duration-300 
                ${isActive ? `${colorClasses[pos.color].active} scale-[1.02] sm:scale-105 shadow-lg` : 'bg-white/50 dark:bg-emerald-950/30 border-emerald-900/10 dark:border-emerald-400/10 opacity-60'}
              `}
            >
              <div className={`text-[11px] sm:text-sm font-bold ${isActive ? colorClasses[pos.color].text : 'text-stone-500 dark:text-emerald-300/50'}`}>{pos.label}</div>
              <div className={`text-[9px] sm:text-[10px] ${isActive ? colorClasses[pos.color].text : 'text-stone-400 dark:text-emerald-400/40'}`}>{pos.description}</div>
              {isActive && (
                <div className="mt-1 sm:mt-1.5 flex items-center gap-0.5 sm:gap-1">
                  <CheckCircle size={10} className={`sm:w-[11px] sm:h-[11px] ${colorClasses[pos.color].text}`} />
                  <span className={`text-[8px] sm:text-[9px] font-semibold ${colorClasses[pos.color].text}`}>{t(`${UP}.youreHere`)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-stone-500 dark:text-emerald-300/60 mt-2 sm:hidden">{t(`${UP}.ficoImpactApprox`)}</p>
    </div>
  );
};

// ===================== PAYDOWN STRATEGY SECTION =====================
const PaydownStrategySection = ({ cards, totalPayTo1 }) => {
  const { t } = useI18n();
  const cardsNeedingPaydown = useMemo(() => {
    return cards
      .filter(c => {
        const payTo1 = c.pay_to_1_percent ?? calcPayTo1Percent(c.balance, c.limit);
        if (payTo1 <= 0) return false;
        if (c.included_in_utilization === false && (c.balance || 0) <= 0) return false;
        return isOpenOrClosedWithBalance(c);
      })
      .sort((a, b) => (b.utilization || 0) - (a.utilization || 0));
  }, [cards]);
  
  if (cardsNeedingPaydown.length === 0) {
    return (
      <GlassCard className="p-4 sm:p-5 mt-3 sm:mt-4">
        <div className="flex items-center gap-2 mb-2 sm:mb-3">
          <Zap size={14} className="text-emerald-600 dark:text-emerald-400 sm:w-4 sm:h-4" />
          <h3 className="text-sm sm:text-base font-bold text-stone-800 dark:text-emerald-50">{t(`${UP}.paydownStrategy`)}</h3>
        </div>
        <div className="text-center py-4 sm:py-6">
          <CheckCircle size={32} className="mx-auto text-emerald-500 mb-2 sm:w-10 sm:h-10" />
          <p className="text-sm sm:text-base text-stone-600 dark:text-emerald-200 font-medium">{t(`${UP}.atOptimalUtilization`)}</p>
          <p className="text-xs sm:text-sm text-stone-500 dark:text-emerald-300/60 mt-1">{t(`${UP}.allCardsBelow1Percent`)}</p>
        </div>
      </GlassCard>
    );
  }
  
  return (
    <GlassCard className="p-4 sm:p-5 mt-3 sm:mt-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-emerald-600 dark:text-emerald-400 sm:w-4 sm:h-4" />
          <h3 className="text-sm sm:text-base font-bold text-stone-800 dark:text-emerald-50">{t(`${UP}.paydownStrategy`)}</h3>
        </div>
        <div className="flex items-center gap-2 sm:text-right">
          <span className="text-xs text-stone-500 dark:text-emerald-300/60">{t(`${UP}.totalToReach1Percent`)}</span>
          <span className="text-base sm:text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(totalPayTo1)}</span>
        </div>
      </div>
      <p className="text-xs sm:text-sm text-stone-600 dark:text-emerald-200/70 mb-2.5 sm:mb-3">{t(`${UP}.paydownOrderHint`)}</p>
      <div className="space-y-2">
        {cardsNeedingPaydown.slice(0, 5).map((card, index) => {
          const payTo1 = calcPayTo1Percent(card.balance, card.limit);
          const payByDate = calcPayByDate(card.statement_date);
          return (
            <div key={card.card_id} className="flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl bg-white/60 dark:bg-emerald-900/30 border border-emerald-900/10 dark:border-emerald-400/10">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-emerald-100 dark:bg-emerald-800/60 flex items-center justify-center text-emerald-700 dark:text-emerald-300 text-xs sm:text-sm font-bold flex-shrink-0">{index + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-xs sm:text-sm text-stone-800 dark:text-emerald-50 truncate">{card.product_name || card.issuer_name || t(`${UP}.cardFallback`)}</div>
                <div className="text-[10px] sm:text-xs text-stone-500 dark:text-emerald-300/60 truncate">{t(`${UP}.utilPercent`, { percent: card.utilization?.toFixed(1) })} {payByDate && `• ${t(`${UP}.payBy`)} ${formatShortMonthDay(payByDate)}`}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-bold text-sm sm:text-base text-emerald-600 dark:text-emerald-400">{formatCurrency(payTo1)}</div>
                <div className="text-[9px] sm:text-[10px] text-stone-500 dark:text-emerald-300/60">{t(`${UP}.to1Percent`)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10px] sm:text-xs text-stone-500 dark:text-emerald-300/50 mt-3 sm:mt-4 italic">{t(`${UP}.resultsVary`)}</p>
    </GlassCard>
  );
};

// ===================== WALLET CARD COMPONENT (MOBILE OPTIMIZED) =====================
const WalletCard = ({ card, onClick }) => {
  const { t } = useI18n();
  const [imageError, setImageError] = useState(false);
  const cardImageUrl = card.custom_card_image_url || card.catalog_card_image_url || card.card_image_url;
  const hasImage = cardImageUrl && !imageError;
  const utilization = parseFloat(card.utilization) || 0;
  const statusConfig = getStatusConfig(utilization, card, t);
  const payTo1Percent = calcPayTo1Percent(card.balance, card.limit);
  const payByDate = calcPayByDate(card.statement_date);
  const rewardsDisplay = card.rewards ? (typeof card.rewards === 'string' ? card.rewards.split(' ')[0] : `${card.rewards}X`) : '—';
  const setupStatus = card.setup_status || 'Ready';
  const needsSetup = setupStatus !== 'Ready';
  const setupConfig = getSetupStatusConfig(setupStatus, t);

  return (
    <article 
      onClick={onClick} 
      className={`
        relative w-full max-w-[340px] sm:max-w-[380px] rounded-[24px] sm:rounded-[32px] overflow-hidden cursor-pointer transition-all duration-200 
        hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.10)] sm:hover:shadow-[0_24px_48px_rgba(0,0,0,0.12)] 
        dark:hover:shadow-[0_24px_48px_rgba(0,0,0,0.35)] 
        bg-gradient-to-b from-[#fcfbfc] to-[#f5f4f5] dark:from-[rgba(10,38,28,0.85)] dark:to-[rgba(8,32,24,0.75)] 
        border shadow-[0_12px_28px_rgba(0,0,0,0.06)] sm:shadow-[0_16px_34px_rgba(0,0,0,0.07)] dark:shadow-[0_16px_34px_rgba(0,0,0,0.25)] 
        ${needsSetup ? 'border-amber-300 dark:border-amber-500/40' : 'border-[#e8e5e6] dark:border-[rgba(142,218,182,0.15)]'}
      `} 
      data-testid={`wallet-card-${card.card_id}`}
    >
      {needsSetup && (
        <div className="absolute top-2.5 sm:top-3 right-2.5 sm:right-3 z-10">
          <span className={`inline-flex items-center gap-1 h-5 sm:h-6 px-1.5 sm:px-2 rounded-full text-[9px] sm:text-[10px] font-bold border ${setupConfig.color}`}>
            <setupConfig.icon size={10} className="sm:w-3 sm:h-3" />{setupConfig.label}
          </span>
        </div>
      )}
      <div className="p-3 sm:p-4 pb-4 sm:pb-5">
        {/* Pocket Area - Responsive Height */}
        <div className="relative h-[130px] sm:h-[160px] rounded-t-[20px] sm:rounded-t-[26px] overflow-hidden bg-gradient-to-br from-[#dff3e8] to-[#d4ecdf] dark:from-[rgba(10,64,45,0.75)] dark:to-[rgba(8,48,35,0.65)]">
          <div 
            className={`
              absolute left-[10px] sm:left-[14px] right-[10px] sm:right-[14px] top-[10px] sm:top-[12px] h-[95px] sm:h-[120px] rounded-[12px] sm:rounded-[14px] overflow-hidden z-[1] 
              shadow-[0_10px_20px_rgba(3,10,22,0.15)] sm:shadow-[0_12px_24px_rgba(3,10,22,0.18)] 
              ${!hasImage ? 'flex flex-col justify-between p-[10px_12px] sm:p-[14px_16px] text-white' : ''}
            `} 
            style={{ background: hasImage ? undefined : getIssuerGradient(card.issuer_name) }}
          >
            {hasImage ? (
              <img src={cardImageUrl.startsWith('/') ? `${BACKEND_URL}${cardImageUrl}` : cardImageUrl} alt={`${card.issuer_name} ${card.product_name}`} className="w-full h-full object-cover" onError={() => setImageError(true)} />
            ) : (
              <>
                <div className="absolute inset-0 pointer-events-none mix-blend-screen opacity-50" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.1), rgba(255,255,255,0)), repeating-linear-gradient(60deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 10px)' }} />
                <div className="relative z-[1] flex justify-between items-start gap-2">
                  <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider opacity-90">{card.issuer_name || t(`${UP}.cardFallback`)}</div>
                  <div className="w-6 sm:w-8 h-4 sm:h-5 rounded-md bg-gradient-to-b from-white/90 to-white/70 shadow-inner" />
                </div>
                <div className="relative z-[1] mt-auto"><div className="text-[14px] sm:text-[18px] font-bold leading-tight">{card.product_name || card.raw_account_name || t(`${UP}.unnamedCard`)}</div></div>
              </>
            )}
          </div>
        </div>
        
        {/* White Body */}
        <div className="relative -mt-0.5 z-[3] rounded-b-[20px] sm:rounded-b-[26px] pt-4 sm:pt-5 px-2.5 sm:px-3 bg-gradient-to-r from-[#faf8f9] via-white to-[#faf8f9] dark:from-[rgba(10,38,28,0.85)] dark:via-[rgba(10,38,28,0.90)] dark:to-[rgba(10,38,28,0.85)]">
          {/* Card Title Row */}
          <div className="relative z-[4] flex justify-between items-start gap-2 mb-1.5 sm:mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-[13px] sm:text-[15px] font-bold text-[#1a1f1c] dark:text-[#EAF7F0] truncate leading-tight">{card.product_name || card.raw_account_name || t(`${UP}.unnamedCard`)}</h3>
              <p className="text-[10px] sm:text-xs text-[#6b7e74] dark:text-[#93AE9F] mt-0.5 truncate">{payTo1Percent > 0 ? t(`${UP}.payToReach1Percent`, { amount: formatCurrency(payTo1Percent) }) : t(`${UP}.atOptimalUtilizationShort`)}</p>
            </div>
            <div className={`shrink-0 inline-flex items-center h-6 sm:h-7 px-2 sm:px-2.5 rounded-full text-[10px] sm:text-[11px] font-bold whitespace-nowrap ${statusConfig.color}`}>{statusConfig.label}</div>
          </div>
          
          {/* Utilization + Progress Bar */}
          <div className="relative z-[4] mb-2.5 sm:mb-3">
            <div className="flex items-baseline gap-1 sm:gap-1.5 mb-1 sm:mb-1.5">
              <span className="text-[26px] sm:text-[32px] font-extrabold tracking-tight text-[#1a1f1c] dark:text-[#EAF7F0] leading-none">{utilization.toFixed(1)}%</span>
              <span className="text-[10px] sm:text-xs text-[#6b7e74] dark:text-[#93AE9F] font-medium">{t(`${UP}.utilizationWord`)}</span>
            </div>
            <UtilizationBar utilization={utilization} />
          </div>
          
          {/* Metrics Grid */}
          <div className="relative z-[4] grid grid-cols-3 gap-1.5 sm:gap-2 mb-2.5 sm:mb-3">
            <div className="bg-[#f8faf9] dark:bg-[rgba(10,38,28,0.65)] border border-[#e8ede9] dark:border-[rgba(142,218,182,0.12)] rounded-lg sm:rounded-xl p-1.5 sm:p-2">
              <p className="text-[9px] sm:text-[10px] text-[#6b7e74] dark:text-[#93AE9F] font-medium mb-0.5">{t(`${UP}.balance`)}</p>
              <p className="text-[11px] sm:text-sm font-bold text-[#1a1f1c] dark:text-[#EAF7F0]">{formatCompactCurrency(card.balance)}</p>
            </div>
            <div className="bg-[#f8faf9] dark:bg-[rgba(10,38,28,0.65)] border border-[#e8ede9] dark:border-[rgba(142,218,182,0.12)] rounded-lg sm:rounded-xl p-1.5 sm:p-2">
              <p className="text-[9px] sm:text-[10px] text-[#6b7e74] dark:text-[#93AE9F] font-medium mb-0.5">{t(`${UP}.limit`)}</p>
              <p className="text-[11px] sm:text-sm font-bold text-[#1a1f1c] dark:text-[#EAF7F0]">{formatCompactCurrency(card.limit)}</p>
            </div>
            <div className="bg-[#f8faf9] dark:bg-[rgba(10,38,28,0.65)] border border-[#e8ede9] dark:border-[rgba(142,218,182,0.12)] rounded-lg sm:rounded-xl p-1.5 sm:p-2">
              <p className="text-[9px] sm:text-[10px] text-[#6b7e74] dark:text-[#93AE9F] font-medium mb-0.5">{t(`${UP}.rewards`)}</p>
              <p className="text-[11px] sm:text-sm font-bold text-[#0d9668] dark:text-emerald-400">{rewardsDisplay}</p>
            </div>
          </div>
          
          {/* Footer Pills */}
          <div className="relative z-[4] pt-2 sm:pt-2.5 border-t border-[#e8ede9] dark:border-[rgba(142,218,182,0.12)] flex items-center gap-1 sm:gap-1.5 flex-wrap">
            <span className="h-6 sm:h-7 px-2 sm:px-2.5 rounded-full border border-[#e6e3e4] dark:border-[rgba(142,218,182,0.12)] bg-[#fbfafb] dark:bg-[rgba(10,38,28,0.65)] text-[#6b7e74] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-semibold inline-flex items-center">•••• {card.masked_last4 || '----'}</span>
            {payByDate && <span className="h-6 sm:h-7 px-2 sm:px-2.5 rounded-full border border-[#d4edda] dark:border-[rgba(52,211,153,0.20)] bg-[#eaf8f0] dark:bg-[rgba(16,185,129,0.15)] text-[#107649] dark:text-emerald-400 text-[10px] sm:text-[11px] font-semibold inline-flex items-center">{t(`${UP}.payBy`)} {formatShortMonthDay(payByDate)}</span>}
            {card.statement_date && <span className="h-6 sm:h-7 px-2 sm:px-2.5 rounded-full border border-[#e6e3e4] dark:border-[rgba(142,218,182,0.12)] bg-[#fbfafb] dark:bg-[rgba(10,38,28,0.65)] text-[#6b7e74] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-semibold inline-flex items-center">{t(`${UP}.stmt`)} {formatShortMonthDay(card.statement_date)}</span>}
            <span className="h-6 sm:h-7 px-2 sm:px-2.5 rounded-full border border-[#e6e3e4] dark:border-[rgba(142,218,182,0.12)] bg-[#fbfafb] dark:bg-[rgba(10,38,28,0.65)] text-[#6b7e74] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-semibold inline-flex items-center">{getSourceLabel(card.source, t)}</span>
          </div>
        </div>
      </div>
    </article>
  );
};

const formatExtractedDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  } catch {
    return dateStr;
  }
};

// ===================== STATEMENT ANALYZER MODAL =====================
const StatementAnalyzerModal = ({
  isOpen,
  onClose,
  mode = 'apply',
  cards = [],
  isDemoMode = false,
  onPrefill,
  onApplied,
  onSyncReport,
}) => {
  const { t } = useI18n();
  const { getAuthHeaders } = useAuth();
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [showCardPicker, setShowCardPicker] = useState(false);

  const resetState = () => {
    setFile(null);
    setAnalysisResult(null);
    setSelectedCardId('');
    setShowCardPicker(false);
    setAnalyzing(false);
    setApplying(false);
  };

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
      if (!validTypes.includes(selectedFile.type)) {
        toast.error('Please upload a JPG, PNG, WEBP, or PDF file');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum 10MB');
        return;
      }
      setFile(selectedFile);
      setAnalysisResult(null);
      setSelectedCardId('');
      setShowCardPicker(false);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/utilization/analyze-statement`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true },
      );
      const data = response.data;
      if (data.success && data.extracted) {
        setAnalysisResult(data);
        const recommended = data.recommended_card_id || '';
        setSelectedCardId(recommended);
        setShowCardPicker(Boolean(data.needs_user_selection && !recommended));
        toast.success('Statement analyzed!');
      } else {
        toast.error(data.detail || data.error || 'Analysis failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze statement');
    } finally {
      setAnalyzing(false);
    }
  };

  const mapExtractedToPrefill = (extracted) => ({
    issuer_name: extracted.issuer_name || '',
    product_name: extracted.card_name || '',
    masked_last4: extracted.last4_digits || '',
    balance: extracted.statement_balance ?? extracted.current_balance ?? 0,
    limit: extracted.credit_limit ?? 0,
    statement_date: extracted.statement_closing_date || '',
    due_date: extracted.payment_due_date || '',
    minimum_payment: extracted.minimum_payment_due ?? null,
  });

  const handlePrefillApply = () => {
    if (!analysisResult?.extracted) return;
    onPrefill?.(mapExtractedToPrefill(analysisResult.extracted));
    onClose();
  };

  const handleAcceptApply = async () => {
    if (isDemoMode) {
      toast.error('Demo cards cannot save statement info. Exit demo mode to apply statement info.');
      return;
    }
    let cardId = selectedCardId || analysisResult?.recommended_card_id;
    if (!cardId && realCards.length === 0 && onSyncReport) {
      const syncedCards = await onSyncReport();
      if (syncedCards.length > 0) {
        const ext = analysisResult?.extracted || {};
        const matched = syncedCards.find((c) => ext.last4_digits && c.masked_last4 === ext.last4_digits);
        cardId = matched?.card_id || analysisResult?.recommended_card_id || syncedCards[0]?.card_id;
      }
    }
    if (!cardId && realCards.length === 0) {
      toast.error('Import your credit report first so we can match this statement to a card.');
      return;
    }
    if (!cardId) {
      toast.error('Select a card to apply statement info.');
      setShowCardPicker(true);
      return;
    }
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/utilization/apply-statement-analysis`,
        { card_id: cardId, extracted: analysisResult.extracted },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      if (response.data.success) {
        const appliedCard = response.data.card;
        const cardLabel = appliedCard?.product_name || appliedCard?.raw_account_name || 'selected card';
        const manualPick = showCardPicker || (analysisResult?.needs_user_selection && cardId !== analysisResult?.recommended_card_id);
        toast.success(
          manualPick
            ? 'Statement info applied to selected card.'
            : `Statement info applied to ${cardLabel}.`,
        );
        onApplied?.(response.data);
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply statement info');
    } finally {
      setApplying(false);
    }
  };

  const extracted = analysisResult?.extracted;
  const matches = analysisResult?.matches || [];
  const recommendedMatch = matches.find((m) => m.card_id === analysisResult?.recommended_card_id);
  const realCards = cards.filter((c) => c.source !== 'Demo' && !String(c.card_id || '').startsWith('demo_'));

  const detailRows = extracted
    ? [
        { label: 'Issuer', value: extracted.issuer_name },
        { label: 'Card / Product', value: extracted.card_name },
        { label: 'Last 4', value: extracted.last4_digits ? `•••• ${extracted.last4_digits}` : null },
        { label: 'Statement Closing Date', value: formatExtractedDate(extracted.statement_closing_date) },
        { label: 'Payment Due Date', value: formatExtractedDate(extracted.payment_due_date) },
        { label: 'Statement Balance', value: extracted.statement_balance != null ? formatCurrency(extracted.statement_balance) : null },
        { label: 'Minimum Payment Due', value: extracted.minimum_payment_due != null ? formatCurrency(extracted.minimum_payment_due) : null },
        { label: 'Credit Limit', value: extracted.credit_limit != null ? formatCurrency(extracted.credit_limit) : null },
      ]
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <FileSearch size={18} className="text-emerald-600 sm:w-5 sm:h-5" />
            {extracted ? 'Statement Details Found' : t(`${UP}.analyzeStatement`)}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {extracted ? 'Review the extracted details before applying them to a card.' : t(`${UP}.analyzeStatementDesc`)}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 p-2.5 sm:p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800">
          <Shield size={14} className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0 sm:w-4 sm:h-4" />
          <p className="text-[10px] sm:text-xs text-emerald-700 dark:text-emerald-300">{t(`${UP}.statementPrivacy`)}</p>
        </div>

        {!extracted ? (
          <div className="space-y-3 sm:space-y-4">
            <div
              className={`border-2 border-dashed rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center cursor-pointer transition-colors ${file ? 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/30' : 'border-stone-300 dark:border-stone-600 hover:border-emerald-400'}`}
              onClick={() => document.getElementById('statement-file-input')?.click()}
            >
              <input id="statement-file-input" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={handleFileChange} />
              {file ? (
                <>
                  <FileText size={28} className="mx-auto text-emerald-600 sm:w-8 sm:h-8" />
                  <p className="font-medium text-xs sm:text-sm text-stone-800 dark:text-stone-200 mt-2 truncate">{file.name}</p>
                  <p className="text-[10px] sm:text-xs text-stone-500 mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                </>
              ) : (
                <>
                  <Upload size={28} className="mx-auto text-stone-400 sm:w-8 sm:h-8" />
                  <p className="font-medium text-xs sm:text-sm text-stone-600 dark:text-stone-300 mt-2">{t(`${UP}.dropStatement`)}</p>
                  <p className="text-[10px] sm:text-xs text-stone-500">{t(`${UP}.fileTypesHint`)}</p>
                </>
              )}
            </div>
            <Button onClick={handleAnalyze} disabled={!file || analyzing} className="w-full h-10 sm:h-11 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
              {analyzing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  {t(`${UP}.analyzing`)}
                </>
              ) : (
                <>
                  <Sparkles size={14} className="mr-2 sm:w-4 sm:h-4" />
                  {t(`${UP}.analyzeStatementBtn`)}
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-sm sm:text-base text-stone-800 dark:text-stone-100">Extracted Details</h4>
                {extracted.confidence != null && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300">
                    {Math.round(extracted.confidence * 100)}% confidence
                  </span>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={resetState} className="h-8 text-xs">
                <Upload size={12} className="mr-1" />
                {t(`${UP}.changeFile`)}
              </Button>
            </div>

            <div className="space-y-2 rounded-xl border border-stone-200 dark:border-stone-700 p-3">
              {detailRows.map(({ label, value }) => (
                <div key={label} className="flex items-start justify-between gap-3 text-xs sm:text-sm">
                  <span className="text-stone-500 dark:text-stone-400">{label}</span>
                  <span className="font-medium text-stone-800 dark:text-stone-100 text-right">{value || '—'}</span>
                </div>
              ))}
            </div>

            {mode === 'apply' && (
              <>
                {isDemoMode && (
                  <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                    Demo mode is active. Exit demo mode to save statement info to a real card.
                  </div>
                )}

                {recommendedMatch && !showCardPicker && (
                  <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs sm:text-sm text-emerald-800 dark:text-emerald-200">
                      Matched to {recommendedMatch.product_name || recommendedMatch.issuer_name}
                      {recommendedMatch.last4 ? ` ending ${recommendedMatch.last4}` : ''}
                    </p>
                  </div>
                )}

                {(showCardPicker || (analysisResult?.needs_user_selection && !recommendedMatch)) && (
                  <div className="space-y-2">
                    <Label className="text-xs sm:text-sm font-semibold">
                      {realCards.length > 0 ? 'Choose a card' : 'No saved cards found'}
                    </Label>
                    {realCards.length > 0 ? (
                      <select
                        value={selectedCardId}
                        onChange={(e) => setSelectedCardId(e.target.value)}
                        className="w-full h-10 rounded-lg border border-stone-300 dark:border-stone-600 bg-white dark:bg-stone-900 px-3 text-sm"
                      >
                        <option value="">Select a card…</option>
                        {realCards.map((card) => (
                          <option key={card.card_id} value={card.card_id}>
                            {(card.product_name || card.raw_account_name || 'Card')}
                            {card.masked_last4 ? ` •••• ${card.masked_last4}` : ''}
                            {card.source ? ` (${card.source})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-stone-500">Import cards from your report or add a card first.</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {extracted && (
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto h-9 sm:h-10 text-sm">
              {t(`${UP}.cancel`)}
            </Button>
            {mode === 'prefill' ? (
              <Button onClick={handlePrefillApply} className="w-full sm:w-auto h-9 sm:h-10 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
                <CheckCircle size={14} className="mr-2" />
                {t(`${UP}.useDetails`)}
              </Button>
            ) : (
              <>
                {recommendedMatch && !showCardPicker && (
                  <Button variant="outline" onClick={() => setShowCardPicker(true)} className="w-full sm:w-auto h-9 sm:h-10 text-sm">
                    Choose Different Card
                  </Button>
                )}
                <Button
                  onClick={handleAcceptApply}
                  disabled={applying || isDemoMode || (realCards.length > 0 && !selectedCardId && !analysisResult?.recommended_card_id)}
                  className="w-full sm:w-auto h-9 sm:h-10 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"
                >
                  <CheckCircle size={14} className="mr-2" />
                  {applying ? 'Applying…' : 'Accept & Apply'}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};


const SortableWalletCard = ({ card, onClick, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.card_id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 20 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="relative w-full max-w-[360px]">
      {!disabled && (
        <button
          type="button"
          className="absolute left-2 top-2 z-20 w-7 h-7 rounded-lg bg-white/90 dark:bg-[rgba(10,38,28,0.85)] border border-stone-200 dark:border-[rgba(142,218,182,0.20)] flex items-center justify-center cursor-grab active:cursor-grabbing"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} className="text-stone-500" />
        </button>
      )}
      <WalletCard card={card} onClick={onClick} />
    </div>
  );
};

const SortableCardsGrid = ({ cards, onClickCard, onReorder, disabled }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || disabled) return;
    const oldIndex = cards.findIndex((c) => c.card_id === active.id);
    const newIndex = cards.findIndex((c) => c.card_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(cards, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={cards.map((c) => c.card_id)} strategy={rectSortingStrategy}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-5 justify-items-center">
          {cards.map((card) => (
            <SortableWalletCard key={card.card_id} card={card} onClick={() => onClickCard(card)} disabled={disabled} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const formatArchiveReason = (reason, accountStatus) => {
  if (reason === 'manual_archive') return 'Manually archived';
  if (reason === 'closed_with_balance') return 'Closed with balance';
  if (reason === 'closed_paid_zero_balance') return 'Paid / $0 balance';
  if (reason === 'closed_account') return 'Closed';
  if (accountStatus === 'closed') return 'Closed';
  return reason || 'Archived';
};

const ArchivedCardsModal = ({ isOpen, onClose, getAuthHeaders, onRestore }) => {
  const { t } = useI18n();
  const [archivedCards, setArchivedCards] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoadingArchived(true);
      try {
        const response = await axios.get(`${API}/utilization/cards?archived=true`, {
          headers: getAuthHeaders(),
          withCredentials: true,
        });
        setArchivedCards(response.data.cards || []);
      } catch {
        toast.error('Failed to load archived cards');
      } finally {
        setLoadingArchived(false);
      }
    };
    load();
  }, [isOpen, getAuthHeaders]);

  const handleRestore = async (cardId) => {
    try {
      await onRestore(cardId);
      setArchivedCards((prev) => prev.filter((c) => c.card_id !== cardId));
      toast.success('Card restored');
    } catch {
      toast.error('Failed to restore card');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Archived Cards</DialogTitle>
          <DialogDescription>Closed, paid, transferred, or manually archived cards. These do not affect utilization totals.</DialogDescription>
        </DialogHeader>
        {loadingArchived ? (
          <Skeleton className="h-24 w-full" />
        ) : archivedCards.length === 0 ? (
          <p className="text-sm text-stone-500 py-6 text-center">No archived cards.</p>
        ) : (
          <div className="space-y-2">
            {archivedCards.map((card) => (
              <div key={card.card_id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-stone-200 dark:border-stone-700">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{card.product_name || card.raw_account_name}</p>
                  <p className="text-xs text-stone-500">
                    {getSourceLabel(card.source, t)} · {formatArchiveReason(card.archive_reason, card.account_status)}
                    {card.masked_last4 ? ` · •••• ${card.masked_last4}` : ''}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleRestore(card.card_id)}>
                  <ArchiveRestore size={14} className="mr-1" />
                  Restore
                </Button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ===================== ADD CARD MODAL =====================
const AddCardModal = ({ isOpen, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({ issuer_name: '', product_name: '', masked_last4: '', balance: '', limit: '', statement_date: '', due_date: '', goal_percent: 9 });
  const [isSaving, setIsSaving] = useState(false);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        balance: initialData.balance != null ? formatMoneyForInput(initialData.balance) : prev.balance,
        limit: initialData.limit != null ? formatMoneyForInput(initialData.limit) : prev.limit,
      }));
    }
  }, [initialData]);
  
  const handleSave = async () => {
    if (!formData.issuer_name) { toast.error('Enter issuer name'); return; }
    const limitValue = parseMoneyValue(formData.limit);
    if (!limitValue || limitValue <= 0) { toast.error('Enter valid credit limit'); return; }
    setIsSaving(true);
    try {
      await onSave({
        ...formData,
        balance: parseMoneyValue(formData.balance),
        limit: limitValue,
        source: 'Manual',
        raw_account_name: `${formData.issuer_name} ${formData.product_name}`.trim(),
      });
      setFormData({ issuer_name: '', product_name: '', masked_last4: '', balance: '', limit: '', statement_date: '', due_date: '', goal_percent: 9 });
      onClose();
    } finally { setIsSaving(false); }
  };
  
  const handleAnalyzerApply = (fields) => {
    setFormData((prev) => ({
      ...prev,
      issuer_name: fields.issuer_name || prev.issuer_name,
      product_name: fields.product_name || prev.product_name,
      balance: fields.balance != null ? formatMoneyForInput(fields.balance) : prev.balance,
      limit: fields.limit != null ? formatMoneyForInput(fields.limit) : prev.limit,
      statement_date: fields.statement_date || prev.statement_date,
      due_date: fields.due_date || prev.due_date,
      masked_last4: fields.masked_last4 || prev.masked_last4,
    }));
    setAnalyzerOpen(false);
  };
  
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] max-w-md p-4 sm:p-6">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-base sm:text-lg"><Plus size={18} className="text-emerald-600 sm:w-5 sm:h-5" />{t(`${UP}.addCard`)}</DialogTitle></DialogHeader>
          <Button variant="outline" onClick={() => setAnalyzerOpen(true)} className="w-full h-9 sm:h-10 text-xs sm:text-sm border-dashed border-emerald-400 text-emerald-700 dark:text-emerald-300"><FileSearch size={14} className="mr-2" />{t(`${UP}.analyzeStatement`)}</Button>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 py-2">
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.issuer`)} *</Label><Input value={formData.issuer_name} onChange={(e) => setFormData(prev => ({ ...prev, issuer_name: e.target.value }))} placeholder="Chase" className="h-9 sm:h-10 text-sm" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.cardName`)}</Label><Input value={formData.product_name} onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))} placeholder="Sapphire" className="h-9 sm:h-10 text-sm" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.balance`)} ($)</Label><Input type="text" inputMode="decimal" placeholder="0.00" value={formData.balance} onChange={(e) => setFormData(prev => ({ ...prev, balance: sanitizeMoneyInput(e.target.value) }))} onBlur={(e) => setFormData(prev => ({ ...prev, balance: normalizeMoneyInput(e.target.value) }))} className="h-9 sm:h-10 text-sm" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.limit`)} ($) *</Label><Input type="text" inputMode="decimal" placeholder="0.00" value={formData.limit} onChange={(e) => setFormData(prev => ({ ...prev, limit: sanitizeMoneyInput(e.target.value) }))} onBlur={(e) => setFormData(prev => ({ ...prev, limit: normalizeMoneyInput(e.target.value) }))} className="h-9 sm:h-10 text-sm" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.last4`)}</Label><Input maxLength={4} value={formData.masked_last4} onChange={(e) => setFormData(prev => ({ ...prev, masked_last4: e.target.value.replace(/\D/g, '') }))} placeholder="1234" className="h-9 sm:h-10 text-sm" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.statementDate`)}</Label><Input type="date" value={formData.statement_date} onChange={(e) => setFormData(prev => ({ ...prev, statement_date: e.target.value }))} className="h-9 sm:h-10 text-sm utilization-date-input" /></div>
            <div className="col-span-2 flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-xs font-semibold">{t(`${UP}.dueDate`)}</Label><Input type="date" value={formData.due_date} onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))} className="h-9 sm:h-10 text-sm utilization-date-input" /></div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={onClose} className="w-full sm:w-auto h-9 sm:h-10 text-sm">{t(`${UP}.cancel`)}</Button>
            <Button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto h-9 sm:h-10 text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white"><Plus size={14} className="mr-2" />{isSaving ? t(`${UP}.adding`) : t(`${UP}.addCard`)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <StatementAnalyzerModal isOpen={analyzerOpen} onClose={() => setAnalyzerOpen(false)} mode="prefill" onPrefill={handleAnalyzerApply} />
    </>
  );
};

// ===================== EDIT DRAWER =====================
const CardDrawer = ({ card, isOpen, onClose, onSave, onArchive }) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  useEffect(() => {
    if (card) {
      setFormData({
        product_name: card.product_name || card.raw_account_name || '',
        issuer_name: card.issuer_name || '',
        masked_last4: card.masked_last4 || '',
        balance: formatMoneyForInput(card.balance),
        limit: formatMoneyForInput(card.limit),
        due_date: card.due_date || '',
        statement_date: card.statement_date || '',
        goal_percent: card.goal_percent || 9,
        rewards: card.rewards || '',
        notes: card.notes || '',
      });
      setHasChanges(false);
    }
  }, [card]);

  const handleChange = (field, value) => { setFormData(prev => ({ ...prev, [field]: value })); setHasChanges(true); };
  const handleMoneyChange = (field, value) => handleChange(field, sanitizeMoneyInput(value));
  const handleMoneyBlur = (field, value) => handleChange(field, normalizeMoneyInput(value));
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(card.card_id, {
        ...formData,
        balance: parseMoneyValue(formData.balance),
        limit: parseMoneyValue(formData.limit),
      });
      setHasChanges(false);
    } finally { setIsSaving(false); }
  };

  const balance = parseMoneyValue(formData.balance);
  const limit = parseMoneyValue(formData.limit);
  const utilization = limit > 0 ? ((balance / limit) * 100) : 0;
  const payTo1Percent = calcPayTo1Percent(balance, limit);
  const payByDate = calcPayByDate(formData.statement_date);
  const statusConfig = getStatusConfig(utilization, card, t);
  
  if (!card) return null;
  
  return (
    <>
      <div className={`fixed inset-0 bg-[rgba(7,18,12,0.34)] dark:bg-[rgba(0,0,0,0.60)] z-50 transition-opacity duration-200 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={onClose} />
      <aside className={`fixed top-0 right-0 w-full sm:w-[460px] max-w-full h-screen bg-white dark:bg-[#0B1F18] z-[60] flex flex-col shadow-[-20px_0_40px_rgba(0,0,0,0.12)] dark:shadow-[-20px_0_40px_rgba(0,0,0,0.40)] transition-transform duration-200 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-4 sm:p-5 border-b border-stone-200 dark:border-[rgba(142,218,182,0.15)] flex justify-between gap-3">
          <div className="min-w-0 flex-1"><h3 className="font-bold text-base sm:text-lg dark:text-[#EAF7F0] truncate">{formData.product_name || t(`${UP}.editCard`)}</h3><p className="text-xs sm:text-sm text-stone-500 dark:text-[#93AE9F]">{card.source === 'Imported' ? t(`${UP}.importedOverrides`) : t(`${UP}.editManualCard`)}</p></div>
          <button onClick={onClose} className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.65)] flex items-center justify-center hover:bg-stone-50 dark:hover:bg-[rgba(142,218,182,0.10)] dark:text-[#C7DDD0] flex-shrink-0"><X size={16} /></button>
        </div>
        <div className="p-4 sm:p-5 overflow-auto flex flex-col gap-3 sm:gap-4 flex-1">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div className="border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-2xl bg-[#fbfefd] dark:bg-[rgba(10,38,28,0.65)] p-2.5 sm:p-3.5"><div className="text-[10px] sm:text-xs text-stone-500 dark:text-[#93AE9F]">{t(`${UP}.utilization`)}</div><div className="text-sm sm:text-base font-extrabold dark:text-[#EAF7F0]">{utilization.toFixed(2)}%</div></div>
            <div className="border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-2xl bg-[#fbfefd] dark:bg-[rgba(10,38,28,0.65)] p-2.5 sm:p-3.5"><div className="text-[10px] sm:text-xs text-stone-500 dark:text-[#93AE9F]">{t(`${UP}.colStatus`)}</div><div className={`text-sm sm:text-base font-extrabold ${statusConfig.label === t(`${UP}.statusHealthy`) ? 'text-emerald-600 dark:text-emerald-400' : 'dark:text-[#EAF7F0]'}`}>{statusConfig.label}</div></div>
            <div className="border border-stone-200 dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-2xl bg-[#fbfefd] dark:bg-[rgba(10,38,28,0.65)] p-2.5 sm:p-3.5"><div className="text-[10px] sm:text-xs text-stone-500 dark:text-[#93AE9F]">{t(`${UP}.colPayBy`)}</div><div className="text-sm sm:text-base font-extrabold dark:text-[#EAF7F0]">{payByDate ? formatShortMonthDay(payByDate) : '—'}</div></div>
            <div className="border border-emerald-200 dark:border-[rgba(16,185,129,0.25)] rounded-xl sm:rounded-2xl bg-emerald-50/50 dark:bg-[rgba(16,185,129,0.15)] p-2.5 sm:p-3.5"><div className="text-[10px] sm:text-xs text-emerald-700 dark:text-emerald-300">{t(`${UP}.payTo1Percent`)}</div><div className="text-sm sm:text-base font-extrabold text-emerald-700 dark:text-emerald-400">{formatCurrency(payTo1Percent)}</div></div>
          </div>
          <UtilizationBar utilization={utilization} />
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5">
            <div className="col-span-2 flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.productName`)}</Label><Input value={formData.product_name} onChange={(e) => handleChange('product_name', e.target.value)} className="h-9 sm:h-11 rounded-xl sm:rounded-[14px] text-sm dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:text-[#EAF7F0]" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.issuer`)}</Label><Input value={formData.issuer_name} onChange={(e) => handleChange('issuer_name', e.target.value)} className="h-9 sm:h-11 rounded-xl sm:rounded-[14px] text-sm dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:text-[#EAF7F0]" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.last4`)}</Label><Input value={formData.masked_last4} onChange={(e) => handleChange('masked_last4', e.target.value.slice(0, 4))} maxLength={4} className="h-9 sm:h-11 rounded-xl sm:rounded-[14px] text-sm dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.15)] dark:text-[#EAF7F0]" /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.balance`)}</Label><Input type="text" inputMode="decimal" placeholder="0.00" value={formData.balance ?? ''} onChange={(e) => handleMoneyChange('balance', e.target.value)} onBlur={(e) => handleMoneyBlur('balance', e.target.value)} className={MONEY_INPUT_CLASS} /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.limit`)}</Label><Input type="text" inputMode="decimal" placeholder="0.00" value={formData.limit ?? ''} onChange={(e) => handleMoneyChange('limit', e.target.value)} onBlur={(e) => handleMoneyBlur('limit', e.target.value)} className={MONEY_INPUT_CLASS} /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.statementDate`)}</Label><Input type="date" value={formData.statement_date} onChange={(e) => handleChange('statement_date', e.target.value)} className={DATE_INPUT_CLASS} /></div>
            <div className="flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.dueDate`)}</Label><Input type="date" value={formData.due_date} onChange={(e) => handleChange('due_date', e.target.value)} className={DATE_INPUT_CLASS} /></div>
            <div className="col-span-2 flex flex-col gap-1.5 sm:gap-2"><Label className="text-[11px] sm:text-[13px] font-bold text-[#4f6258] dark:text-[#93AE9F]">{t(`${UP}.notes`)}</Label><textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} className="min-h-[70px] sm:min-h-[80px] rounded-xl sm:rounded-[14px] border border-stone-200 dark:border-[rgba(142,218,182,0.15)] p-2.5 sm:p-3 text-sm resize-y dark:bg-[rgba(10,38,28,0.65)] dark:text-[#EAF7F0]" /></div>
          </div>
        </div>
        <div className="p-3 sm:p-4 border-t border-stone-200 dark:border-[rgba(142,218,182,0.15)] flex flex-col sm:flex-row justify-between gap-2 sm:gap-2.5 bg-white dark:bg-[#0B1F18]">
          <Button variant="destructive" onClick={() => onArchive(card.card_id)} className="gap-2 h-9 sm:h-10 text-sm order-2 sm:order-1"><Archive size={14} />Archive Card</Button>
          <div className="flex gap-2 order-1 sm:order-2">
            <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none h-9 sm:h-10 text-sm dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">{t(`${UP}.cancel`)}</Button>
            <Button onClick={handleSave} disabled={!hasChanges || isSaving} className="flex-1 sm:flex-none gap-2 h-9 sm:h-10 text-sm bg-gradient-to-b from-[#1ac06f] to-[#0fa85e] border-0"><Save size={14} />{isSaving ? t(`${UP}.saving`) : t(`${UP}.save`)}</Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// (MobileCardRow removed - using horizontally scrollable table for all devices)

// ===================== MAIN COMPONENT =====================
const UtilizationManager = () => {
  const { t } = useI18n();
  const { getAuthHeaders } = useAuth();
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState({ imported: {}, manual: {} });
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoCards, setDemoCards] = useState([]);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [archivedModalOpen, setArchivedModalOpen] = useState(false);

  const fetchCards = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/utilization/cards?auto_sync=true`, { headers: getAuthHeaders(), withCredentials: true });
      const fetchedCards = response.data.cards || [];
      setCards(fetchedCards);
      setSummary(response.data.summary || { imported: {}, manual: {} });
    } catch (error) { console.error('Error fetching cards:', error); toast.error('Failed to load cards'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchCards(); }, [fetchCards]);

  const handleExitDemo = async () => {
    setIsDemoMode(false);
    setDemoCards([]);
    setLoading(true);
    await fetchCards();
  };

  const handleSyncFromReport = async (silent = false) => {
    try {
      const response = await axios.post(`${API}/utilization/import-from-report`, {}, { headers: getAuthHeaders(), withCredentials: true });
      const data = response.data;
      setIsDemoMode(false);
      setDemoCards([]);
      await fetchCards();
      if (!silent) {
        const totalRevolving = data.total_revolving_count ?? 0;
        if (totalRevolving === 0) toast.info(data.message || 'No revolving credit cards were found in this report.');
        else toast.success(data.message || `Synced ${totalRevolving} revolving cards from your credit report.`);
      }
      return data.cards || [];
    } catch (error) {
      if (!silent) {
        if (error.response?.status === 404) toast.error('Import your credit report first so we can sync your cards.');
        else toast.error(error.response?.data?.detail || 'Failed to sync cards from report');
      }
      return [];
    }
  };

  const handleImportFromReport = () => handleSyncFromReport(false);

  const handleImportDemo = async () => {
    const demoDemoCards = [
      { card_id: 'demo_1', source: 'Demo', issuer_name: 'Chase', product_name: 'Sapphire Reserve', masked_last4: '4521', balance: 2847.50, limit: 25000, utilization: 11.39, statement_date: '2026-04-15', due_date: '2026-05-10', included_in_utilization: true, setup_status: 'Ready' },
      { card_id: 'demo_2', source: 'Demo', issuer_name: 'American Express', product_name: 'Gold Card', masked_last4: '1003', balance: 1250.00, limit: 15000, utilization: 8.33, statement_date: '2026-04-20', due_date: '2026-05-15', included_in_utilization: true, setup_status: 'Ready' },
      { card_id: 'demo_3', source: 'Demo', issuer_name: 'Capital One', product_name: 'Venture X', masked_last4: '7789', balance: 4520.75, limit: 30000, utilization: 15.07, statement_date: '2026-04-25', due_date: '2026-05-20', included_in_utilization: true, setup_status: 'Ready' },
    ];
    setDemoCards(demoDemoCards);
    setIsDemoMode(true);
    toast.success('Demo cards loaded.');
  };

  const handleAddCard = async (cardData) => {
    try {
      const response = await axios.post(`${API}/utilization/cards`, cardData, { headers: getAuthHeaders(), withCredentials: true });
      if (response.data.success) {
        toast.success('Card added!');
        setIsDemoMode(false);
        setDemoCards([]);
        await fetchCards();
      }
    } catch (error) { toast.error('Failed to add card'); }
  };

  const handleUpdateCard = async (cardId, updates) => {
    try {
      const response = await axios.patch(`${API}/utilization/cards/${cardId}`, updates, { headers: getAuthHeaders(), withCredentials: true });
      if (response.data.success) { toast.success('Card updated!'); await fetchCards(); setSelectedCard(response.data.card); }
    } catch (error) { toast.error('Failed to update card'); }
  };

  const handleArchiveCard = async (cardId) => {
    if (!window.confirm('Archive this card? It will move to Archived Cards and no longer count toward utilization.')) return;
    try {
      await axios.post(`${API}/utilization/cards/${cardId}/archive`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success('Card archived');
      setDrawerOpen(false);
      setSelectedCard(null);
      await fetchCards();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to archive card'); }
  };

  const handleRestoreCard = async (cardId) => {
    await axios.post(`${API}/utilization/cards/${cardId}/restore`, {}, { headers: getAuthHeaders(), withCredentials: true });
    await fetchCards();
  };

  const handleReorderCards = async (reorderedCards) => {
    const withOrder = reorderedCards.map((card, index) => ({ ...card, display_order: index }));
    setCards(withOrder);
    try {
      await axios.post(
        `${API}/utilization/cards/reorder`,
        { cards: withOrder.map((c, index) => ({ card_id: c.card_id, display_order: index })) },
        { headers: getAuthHeaders(), withCredentials: true },
      );
    } catch {
      toast.error('Failed to save card order');
      await fetchCards();
    }
  };

  const openCardDrawer = (card) => { setSelectedCard(card); setDrawerOpen(true); };

  const displayCards = isDemoMode && demoCards.length > 0 ? demoCards : cards;
  const displaySummary = useMemo(() => {
    if (isDemoMode && demoCards.length > 0) {
      const totalBalance = demoCards.reduce((sum, c) => sum + (c.balance || 0), 0);
      const totalLimit = demoCards.reduce((sum, c) => sum + (c.limit || 0), 0);
      const utilization = totalLimit > 0 ? (totalBalance / totalLimit * 100) : (totalBalance > 0 ? null : 0);
      const payTo1 = Math.max(0, totalBalance - (totalLimit * 0.01));
      return {
        total_balance: totalBalance,
        total_limit: totalLimit,
        overall_utilization: utilization,
        recommended_paydown: payTo1,
        count: demoCards.length,
        is_high_risk: totalLimit === 0 && totalBalance > 0,
        closed_with_balance_count: demoCards.filter(isClosedWithBalance).length,
      };
    }
    const imported = summary.imported || {};
    const manual = summary.manual || {};
    const totalBalance = (imported.total_balance || 0) + (manual.total_balance || 0);
    const totalLimit = (imported.total_limit || 0) + (manual.total_limit || 0);
    const isHighRisk = imported.is_high_risk || manual.is_high_risk || (totalLimit === 0 && totalBalance > 0);
    const overallUtil = isHighRisk
      ? null
      : (totalLimit > 0 ? (totalBalance / totalLimit * 100) : 0);
    return {
      total_balance: totalBalance,
      total_limit: totalLimit,
      overall_utilization: overallUtil,
      recommended_paydown: (imported.recommended_paydown || 0) + (manual.recommended_paydown || 0),
      count: (imported.count || 0) + (manual.count || 0),
      is_high_risk: isHighRisk,
      closed_with_balance_count: (imported.closed_with_balance_count || 0) + (manual.closed_with_balance_count || 0),
    };
  }, [isDemoMode, demoCards, summary]);

  const cardsNeedingSetup = displayCards.filter(c => c.setup_status && c.setup_status !== 'Ready');
  const closedWithBalanceCards = displayCards.filter(isClosedWithBalance);
  const position = getUtilizationPosition(displaySummary.overall_utilization ?? (displaySummary.is_high_risk ? 150 : 0), t);

  if (loading) {
    return <div className="space-y-4 sm:space-y-6 p-3 sm:p-4" data-testid="utilization-loading"><Skeleton className="h-10 sm:h-12 w-48 sm:w-64" /><Skeleton className="h-48 sm:h-64 rounded-2xl" /><Skeleton className="h-72 sm:h-96 rounded-2xl" /></div>;
  }

  return (
    <div className="space-y-3 sm:space-y-[18px] p-3 sm:p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="utilization-strategy-planner">
      {/* ===== HEADER ===== */}
      <div className="flex flex-col gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-stone-900 dark:text-emerald-50">{t(`${UP}.title`)}</h1>
          <p className="text-xs sm:text-sm text-stone-600 dark:text-emerald-200/70 mt-1 max-w-xl">{t(`${UP}.subtitle`)}</p>
        </div>
        {/* Mobile: Stack buttons vertically, Desktop: horizontal */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
          <Button variant="outline" onClick={handleImportFromReport} className="h-9 sm:h-10 text-[11px] sm:text-sm border-emerald-200 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" data-testid="import-from-report-btn">
            <FileText size={14} className="mr-1.5 sm:mr-2 sm:w-4 sm:h-4" /><span className="hidden xs:inline">Sync Report Cards</span><span className="xs:hidden">Sync</span>
          </Button>
          <Button variant="outline" onClick={() => setAnalyzerOpen(true)} className="h-9 sm:h-10 text-[11px] sm:text-sm border-emerald-200 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" data-testid="analyze-statement-btn">
            <FileSearch size={14} className="mr-1.5 sm:mr-2 sm:w-4 sm:h-4" />{t(`${UP}.analyze`)}
          </Button>
          {!isDemoMode && (
            <Button variant="outline" onClick={handleImportDemo} className="h-9 sm:h-10 text-[11px] sm:text-sm" data-testid="import-demo-btn">
              <Download size={14} className="mr-1.5 sm:mr-2" />{t(`${UP}.demo`)}
            </Button>
          )}
          <Button onClick={() => setAddModalOpen(true)} className="h-9 sm:h-10 text-[11px] sm:text-sm bg-gradient-to-b from-[#1ac06f] to-[#0fa85e] border-0 text-white col-span-2 sm:col-span-1" data-testid="add-card-btn">
            <Plus size={14} className="mr-1.5 sm:mr-2" />{t(`${UP}.addCard`)}
          </Button>
        </div>
      </div>

      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="flex items-start gap-2 flex-1">
            <AlertCircle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5 sm:w-[18px] sm:h-[18px]" />
            <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300">{t(`${UP}.demoBanner`)}</p>
          </div>
          <Button size="sm" variant="outline" onClick={handleExitDemo} className="shrink-0 text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-600 h-8 text-xs w-full sm:w-auto">Exit Demo</Button>
        </div>
      )}

      {/* ===== SUMMARY SECTION ===== */}
      {displayCards.length > 0 && (
        <GlassCard highlight className="p-3 sm:p-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-4">
            <div className="min-h-[70px] sm:min-h-[90px] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white/60 dark:bg-emerald-900/30 border border-emerald-900/10 dark:border-emerald-400/10 flex flex-col justify-center">
              <div className="flex items-center gap-1 sm:gap-1.5 text-stone-500 dark:text-emerald-300/60 mb-0.5 sm:mb-1"><DollarSign size={12} className="sm:w-[14px] sm:h-[14px]" /><span className="text-[10px] sm:text-xs font-semibold uppercase">{t(`${UP}.balance`)}</span></div>
              <div className="text-base sm:text-xl font-bold text-stone-900 dark:text-emerald-50">{formatCurrency(displaySummary.total_balance)}</div>
            </div>
            <div className="min-h-[70px] sm:min-h-[90px] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white/60 dark:bg-emerald-900/30 border border-emerald-900/10 dark:border-emerald-400/10 flex flex-col justify-center">
              <div className="flex items-center gap-1 sm:gap-1.5 text-stone-500 dark:text-emerald-300/60 mb-0.5 sm:mb-1"><CreditCardIcon size={12} className="sm:w-[14px] sm:h-[14px]" /><span className="text-[10px] sm:text-xs font-semibold uppercase">{t(`${UP}.limit`)}</span></div>
              <div className="text-base sm:text-xl font-bold text-stone-900 dark:text-emerald-50">{formatCurrency(displaySummary.total_limit)}</div>
            </div>
            <div className="min-h-[70px] sm:min-h-[90px] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-white/60 dark:bg-emerald-900/30 border border-emerald-900/10 dark:border-emerald-400/10 flex flex-col justify-center">
              <div className="flex items-center gap-1 sm:gap-1.5 text-stone-500 dark:text-emerald-300/60 mb-0.5 sm:mb-1"><Percent size={12} className="sm:w-[14px] sm:h-[14px]" /><span className="text-[10px] sm:text-xs font-semibold uppercase">{t(`${UP}.utilization`)}</span></div>
              <div className="flex items-baseline gap-1 sm:gap-2 flex-wrap">
                {displaySummary.is_high_risk ? (
                  <>
                    <span className="text-base sm:text-xl font-bold text-orange-700 dark:text-orange-300">{t(`${UP}.statusHighRisk`, null, 'High Risk')}</span>
                    <span className="text-[9px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300">{t(`${UP}.statusNeedsAttention`, null, 'Needs Attention')}</span>
                  </>
                ) : (
                  <>
                    <span className="text-base sm:text-xl font-bold text-stone-900 dark:text-emerald-50">{(displaySummary.overall_utilization ?? 0).toFixed(1)}%</span>
                    <span className={`text-[9px] sm:text-[11px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full ${getStatusConfig(displaySummary.overall_utilization, null, t).color}`}>{position.label}</span>
                  </>
                )}
              </div>
            </div>
            <div className="min-h-[70px] sm:min-h-[90px] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-50/90 to-white/70 dark:from-emerald-900/50 dark:to-emerald-950/30 border border-emerald-500/25 dark:border-emerald-400/20 flex flex-col justify-center">
              <div className="flex items-center gap-1 sm:gap-1.5 text-emerald-600 dark:text-emerald-400 mb-0.5 sm:mb-1"><Target size={12} className="sm:w-[14px] sm:h-[14px]" /><span className="text-[10px] sm:text-xs font-semibold uppercase">{t(`${UP}.payTo1Percent`)}</span></div>
              <div className="text-base sm:text-xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(displaySummary.recommended_paydown)}</div>
            </div>
          </div>
          <UtilizationBar utilization={displaySummary.is_high_risk ? 100 : (displaySummary.overall_utilization ?? 0)} />
          <UtilizationPositionCards currentUtilization={displaySummary.is_high_risk ? 150 : (displaySummary.overall_utilization ?? 0)} />
        </GlassCard>
      )}

      {/* ===== CLOSED WITH BALANCE BANNER ===== */}
      {closedWithBalanceCards.length > 0 && !isDemoMode && (
        <div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800" data-testid="closed-balance-banner">
          <div className="flex items-start gap-2 sm:gap-3">
            <AlertCircle size={16} className="text-orange-600 dark:text-orange-400 shrink-0 mt-0.5 sm:w-[18px] sm:h-[18px]" />
            <div>
              <p className="text-xs sm:text-sm font-semibold text-orange-800 dark:text-orange-200">{t(`${UP}.closedBalanceBannerTitle`, null, 'Closed cards with balances affect utilization')}</p>
              <p className="text-xs sm:text-sm text-orange-700 dark:text-orange-300 mt-1">{t(`${UP}.closedBalanceTooltip`, null, 'Closed card limit is not counted, but the balance still affects utilization.')}</p>
              <p className="text-[10px] sm:text-xs text-orange-600 dark:text-orange-400 mt-1">{t(`${UP}.closedBalanceCount`, null, '{{count}} closed card(s) with remaining balance', { count: closedWithBalanceCards.length })}</p>
            </div>
          </div>
        </div>
      )}

      {/* ===== PAYDOWN STRATEGY ===== */}
      {displayCards.length > 0 && <PaydownStrategySection cards={displayCards} totalPayTo1={displaySummary.recommended_paydown} />}

      {/* ===== SETUP BANNER ===== */}
      {cardsNeedingSetup.length > 0 && !isDemoMode && (
        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded-xl sm:rounded-[22px] p-3 sm:p-4 shadow-sm" data-testid="setup-banner">
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center shrink-0"><Settings size={16} className="text-amber-600 dark:text-amber-400 sm:w-5 sm:h-5" /></div>
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-sm sm:text-base text-amber-900 dark:text-amber-200 mb-1">{t(`${UP}.finishSetupTitle`)}</h4>
              <p className="text-xs sm:text-sm text-amber-700 dark:text-amber-300 mb-2 sm:mb-3">{t(`${UP}.finishSetupBody`)}</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {cardsNeedingSetup.slice(0, 4).map(card => {
                  const setupConfig = getSetupStatusConfig(card.setup_status, t);
                  const StatusIcon = setupConfig.icon;
                  return (<button key={card.card_id} onClick={() => openCardDrawer(card)} className={`inline-flex items-center gap-1 h-7 sm:h-8 px-2 sm:px-3 rounded-full text-[10px] sm:text-xs font-semibold border transition-colors hover:opacity-80 ${setupConfig.color}`}><StatusIcon size={12} /><span className="truncate max-w-[80px] sm:max-w-[120px]">{card.issuer_name || t(`${UP}.unknown`)}</span></button>);
                })}
                {cardsNeedingSetup.length > 4 && <span className="inline-flex items-center h-7 sm:h-8 px-2 text-[10px] sm:text-xs text-amber-600 dark:text-amber-400 font-medium">{t(`${UP}.moreCards`, { count: cardsNeedingSetup.length - 4 })}</span>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== MY CARDS SECTION ===== */}
      <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[#dcebe3] dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-[22px] shadow-[0_12px_28px_rgba(8,38,24,0.06)] sm:shadow-[0_16px_38px_rgba(8,38,24,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.25)] overflow-hidden">
        <div className="p-3 sm:p-[18px] sm:pb-0 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="m-0 text-base sm:text-lg font-extrabold dark:text-[#EAF7F0]">{t(`${UP}.myCards`)}</h3>
            <p className="m-0 mt-1 text-xs sm:text-sm text-[#6b7e74] dark:text-[#93AE9F]">{t(`${UP}.cardsCount`, { count: displayCards.length })}{!isDemoMode ? ' · Drag cards to reorder' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            {!isDemoMode && (
              <Button variant="outline" size="sm" onClick={() => setArchivedModalOpen(true)} className="h-8 sm:h-9 text-xs">
                <Archive size={14} className="mr-1.5" />
                Archived Cards
              </Button>
            )}
            <Button onClick={() => setAddModalOpen(true)} className="h-8 sm:h-11 px-3 sm:px-[18px] rounded-xl sm:rounded-2xl bg-gradient-to-b from-[#1ac06f] to-[#0fa85e] border-0 font-bold text-xs sm:text-sm"><Plus size={14} className="mr-1.5 sm:mr-2" />{t(`${UP}.add`)}</Button>
          </div>
        </div>
        <div className="p-3 sm:p-[18px]">
          {displayCards.length === 0 ? (
            <div className="border border-dashed border-[#cfe3d8] dark:border-[rgba(142,218,182,0.20)] bg-[#f8fcfa] dark:bg-[rgba(10,38,28,0.50)] rounded-xl sm:rounded-[20px] p-5 sm:p-7 text-center">
              <CreditCard size={40} className="mx-auto text-stone-300 dark:text-[#93AE9F] mb-2 sm:w-12 sm:h-12" />
              <h3 className="font-semibold text-sm sm:text-base mb-1 dark:text-[#EAF7F0]">{t(`${UP}.noCardsYet`)}</h3>
              <p className="text-xs sm:text-sm text-stone-500 dark:text-[#93AE9F] mb-3">{t(`${UP}.importOrAddManually`)}</p>
              <div className="flex flex-col sm:flex-row justify-center gap-2">
                <Button variant="outline" onClick={handleImportDemo} className="h-9 sm:h-11 rounded-xl sm:rounded-2xl text-xs sm:text-sm dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">{t(`${UP}.importDemo`)}</Button>
                <Button onClick={() => setAddModalOpen(true)} className="h-9 sm:h-11 rounded-xl sm:rounded-2xl text-xs sm:text-sm bg-gradient-to-b from-[#1ac06f] to-[#0fa85e] border-0">{t(`${UP}.addCard`)}</Button>
              </div>
            </div>
          ) : (
            <SortableCardsGrid
              cards={displayCards}
              onClickCard={(card) => !isDemoMode && openCardDrawer(card)}
              onReorder={handleReorderCards}
              disabled={isDemoMode}
            />
          )}
        </div>
      </div>

      {/* ===== ALL CARDS TABLE - Horizontally scrollable on all devices ===== */}
      <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] border border-[#dcebe3] dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-[22px] shadow-[0_12px_28px_rgba(8,38,24,0.06)] sm:shadow-[0_16px_38px_rgba(8,38,24,0.08)] dark:shadow-[0_16px_38px_rgba(0,0,0,0.25)] overflow-hidden" data-testid="all-cards-section">
        <div className="p-3 sm:p-[18px] sm:pb-0">
          <h3 className="m-0 text-base sm:text-lg font-extrabold dark:text-[#EAF7F0]">{t(`${UP}.allCards`)}</h3>
          <p className="m-0 mt-1 text-xs sm:text-sm text-[#6b7e74] dark:text-[#93AE9F]">{t(`${UP}.tableHint`)}</p>
        </div>
        
        <div className="p-3 sm:p-[18px]">
          {/* Horizontal scroll wrapper for mobile compatibility */}
          <div className="w-full overflow-x-auto border border-[#dcebe3] dark:border-[rgba(142,218,182,0.15)] rounded-xl sm:rounded-[18px] bg-white dark:bg-[rgba(10,38,28,0.50)]">
            <table className="w-full min-w-[1360px] border-collapse" data-testid="cards-table">
              <thead>
                <tr>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] w-[150px] max-w-[190px]">{t(`${UP}.colSource`, null, 'Source')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colCard`, null, 'Card')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colIssuer`, null, 'Issuer')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colAccountStatus`, null, 'Account Status')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colBalance`, null, 'Balance')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colLimit`, null, 'Limit')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colUtilPercent`, null, 'Util %')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colHealth`, null, 'Health')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colSetup`, null, 'Setup')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colPayBy`, null, 'Pay By')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colStatement`, null, 'Statement Date')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colDueDate`, null, 'Due Date')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colPayTo1Percent`, null, 'Pay to 1%')}</th>
                  <th className="sticky top-0 bg-[#f7fbf9] dark:bg-[rgba(8,32,24,0.80)] text-[#5c6d64] dark:text-[#93AE9F] text-[10px] sm:text-[11px] font-bold text-left p-2 sm:p-[10px_8px] border-b border-[#dcebe3] dark:border-[rgba(142,218,182,0.12)] whitespace-nowrap">{t(`${UP}.colActions`, null, 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {displayCards.length === 0 ? (
                  <tr><td colSpan={14} className="p-4 text-center text-stone-500 dark:text-[#93AE9F] text-sm">{t(`${UP}.emptyTable`)}</td></tr>
                ) : displayCards.map(card => {
                  const payTo1 = card.pay_to_1_percent ?? calcPayTo1Percent(card.balance, card.limit);
                  const payBy = getPayByDisplay(card.statement_date);
                  const accountStatusLabel = getAccountStatusLabel(card, t);
                  const healthConfig = getHealthStatusConfig(card, t);
                  const setupConfig = getSetupStatusConfig(card.setup_status, t);
                  const cardName = card.product_name || card.raw_account_name || t(`${UP}.unnamed`);
                  return (
                    <tr 
                      key={card.card_id} 
                      className="border-b border-[#edf4ef] dark:border-[rgba(142,218,182,0.08)] hover:bg-stone-50 dark:hover:bg-[rgba(142,218,182,0.08)] cursor-pointer transition-colors" 
                      onClick={() => !isDemoMode && openCardDrawer(card)}
                      data-testid={`table-row-${card.card_id}`}
                    >
                      <td className="p-2 align-top w-[150px] max-w-[190px]">
                        <span className={`inline-flex max-w-[180px] px-1.5 py-0.5 rounded-md text-[9px] sm:text-[10px] font-semibold leading-tight ${getSourceBadgeClass(card.source)}`}>
                          {getSourceLabel(card.source, t)}
                        </span>
                      </td>
                      <td className="p-2 font-medium text-[11px] sm:text-xs dark:text-[#EAF7F0] max-w-[120px] sm:max-w-[160px] truncate" title={cardName}>{cardName}</td>
                      <td className="p-2 text-[11px] sm:text-xs dark:text-[#C7DDD0] max-w-[100px] truncate" title={card.issuer_name || ''}>{card.issuer_name || '—'}</td>
                      <td className="p-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[9px] sm:text-[10px] font-semibold whitespace-nowrap ${getAccountStatusBadgeClass(accountStatusLabel)}`}>
                          {accountStatusLabel}
                        </span>
                      </td>
                      <td className="p-2 text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap tabular-nums">{formatFullCurrency(card.balance)}</td>
                      <td className="p-2 text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap tabular-nums">{formatFullCurrency(card.limit)}</td>
                      <td className="p-2 font-bold text-[11px] sm:text-xs dark:text-[#EAF7F0] whitespace-nowrap tabular-nums">{card.utilization != null ? `${Number(card.utilization).toFixed(1)}%` : '—'}</td>
                      <td className="p-2">
                        {healthConfig.label === '—' ? (
                          <span className="text-[11px] sm:text-xs text-stone-400">—</span>
                        ) : (
                          <span
                            className={`inline-flex px-1.5 py-0.5 rounded-md text-[9px] sm:text-[10px] font-semibold whitespace-nowrap ${healthConfig.badge}`}
                            title={healthConfig.tooltip || card.utilization_note || undefined}
                          >
                            {healthConfig.label}
                          </span>
                        )}
                      </td>
                      <td className="p-2">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[8px] sm:text-[9px] font-semibold border whitespace-nowrap ${setupConfig.color}`}>{setupConfig.label}</span>
                      </td>
                      <td className="p-2">
                        <div className="flex flex-col">
                          <span className={payBy.className}>{payBy.label}</span>
                          {payBy.status === 'today' && (
                            <span className="text-[9px] sm:text-[10px] font-semibold text-amber-700 dark:text-amber-300 leading-tight">
                              Today
                            </span>
                          )}
                          {payBy.status === 'overdue' && (
                            <span className="text-[9px] sm:text-[10px] font-semibold text-red-600 dark:text-red-300 leading-tight">
                              Overdue
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-2 text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap">{card.statement_date ? formatShortMonthDay(card.statement_date) : '—'}</td>
                      <td className="p-2 text-[11px] sm:text-xs dark:text-[#C7DDD0] whitespace-nowrap">{card.due_date ? formatShortMonthDay(card.due_date) : '—'}</td>
                      <td className="p-2 text-[11px] sm:text-xs font-semibold text-emerald-700 dark:text-emerald-400 whitespace-nowrap tabular-nums">{formatFullCurrency(payTo1)}</td>
                      <td className="p-2 whitespace-nowrap">
                        {!isDemoMode && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[10px] sm:text-xs"
                            onClick={(e) => { e.stopPropagation(); openCardDrawer(card); }}
                          >
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {displayCards.length > 0 && (
            <p className="text-[10px] text-stone-400 dark:text-emerald-300/50 mt-2 text-center sm:hidden">← Swipe to see all columns →</p>
          )}
        </div>
      </div>

      {/* ===== MODALS & DRAWERS ===== */}
      <CardDrawer card={selectedCard} isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onSave={handleUpdateCard} onArchive={handleArchiveCard} />
      <AddCardModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} onSave={handleAddCard} />
      <ArchivedCardsModal isOpen={archivedModalOpen} onClose={() => setArchivedModalOpen(false)} getAuthHeaders={getAuthHeaders} onRestore={handleRestoreCard} />
      <StatementAnalyzerModal
        isOpen={analyzerOpen}
        onClose={() => setAnalyzerOpen(false)}
        mode="apply"
        cards={cards}
        isDemoMode={isDemoMode}
        onApplied={() => fetchCards()}
        onSyncReport={() => handleSyncFromReport(true)}
      />
    </div>
  );
};

export default UtilizationManager;
